/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CompanyAppSettings } from '../models/CompanyAppSettings';
import type { CompanyAppSettingsRequest } from '../models/CompanyAppSettingsRequest';
import type { CompanyBAVSettings } from '../models/CompanyBAVSettings';
import type { CompanyBAVSettingsPatch } from '../models/CompanyBAVSettingsPatch';
import type { CompanyBAVSettingsPatchRequest } from '../models/CompanyBAVSettingsPatchRequest';
import type { CompanyBenefitSettings } from '../models/CompanyBenefitSettings';
import type { CompanyContactData } from '../models/CompanyContactData';
import type { CompanyContactDataRequest } from '../models/CompanyContactDataRequest';
import type { CompanyCustomBenefit } from '../models/CompanyCustomBenefit';
import type { CompanyCustomBenefitDetail } from '../models/CompanyCustomBenefitDetail';
import type { CompanyCustomBenefitPatch } from '../models/CompanyCustomBenefitPatch';
import type { CompanyCustomBenefitPatchRequest } from '../models/CompanyCustomBenefitPatchRequest';
import type { CompanyCustomBenefitRequest } from '../models/CompanyCustomBenefitRequest';
import type { CompanyExtended } from '../models/CompanyExtended';
import type { CompanyExtendedRequest } from '../models/CompanyExtendedRequest';
import type { CompanyGeneralData } from '../models/CompanyGeneralData';
import type { CompanyGeneralDataRequest } from '../models/CompanyGeneralDataRequest';
import type { CompanyJobbikeSettings } from '../models/CompanyJobbikeSettings';
import type { CompanyJobbikeSettingsPatch } from '../models/CompanyJobbikeSettingsPatch';
import type { CompanyJobbikeSettingsPatchRequest } from '../models/CompanyJobbikeSettingsPatchRequest';
import type { CompanyMPPSettings } from '../models/CompanyMPPSettings';
import type { CompanyMPPSettingsRequest } from '../models/CompanyMPPSettingsRequest';
import type { CompanyPaymentData } from '../models/CompanyPaymentData';
import type { CompanyPaymentDataRequest } from '../models/CompanyPaymentDataRequest';
import type { CompanySettings } from '../models/CompanySettings';
import type { CompanySettingsRequest } from '../models/CompanySettingsRequest';
import type { ForceKomboSync } from '../models/ForceKomboSync';
import type { HrisSetupStatus } from '../models/HrisSetupStatus';
import type { KomboLink } from '../models/KomboLink';
import type { KomboSync } from '../models/KomboSync';
import type { KomboSyncRequest } from '../models/KomboSyncRequest';
import type { PaginatedCompanyExtendedList } from '../models/PaginatedCompanyExtendedList';
import type { PaginatedKomboSyncList } from '../models/PaginatedKomboSyncList';
import type { PatchedCompanyAppSettingsRequest } from '../models/PatchedCompanyAppSettingsRequest';
import type { PatchedCompanyBAVSettingsPatchRequest } from '../models/PatchedCompanyBAVSettingsPatchRequest';
import type { PatchedCompanyContactDataRequest } from '../models/PatchedCompanyContactDataRequest';
import type { PatchedCompanyCustomBenefitPatchRequest } from '../models/PatchedCompanyCustomBenefitPatchRequest';
import type { PatchedCompanyCustomBenefitRequest } from '../models/PatchedCompanyCustomBenefitRequest';
import type { PatchedCompanyExtendedRequest } from '../models/PatchedCompanyExtendedRequest';
import type { PatchedCompanyGeneralDataRequest } from '../models/PatchedCompanyGeneralDataRequest';
import type { PatchedCompanyJobbikeSettingsPatchRequest } from '../models/PatchedCompanyJobbikeSettingsPatchRequest';
import type { PatchedCompanyMPPSettingsRequest } from '../models/PatchedCompanyMPPSettingsRequest';
import type { PatchedCompanyPaymentDataRequest } from '../models/PatchedCompanyPaymentDataRequest';
import type { PatchedCompanySettingsRequest } from '../models/PatchedCompanySettingsRequest';
import type { PatchedKomboSyncRequest } from '../models/PatchedKomboSyncRequest';
import type { PatchedPayrollExportMappingRequest } from '../models/PatchedPayrollExportMappingRequest';
import type { PayrollExportMapping } from '../models/PayrollExportMapping';
import type { PayrollExportMappingRequest } from '../models/PayrollExportMappingRequest';
import type { ReferralLink } from '../models/ReferralLink';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class CompaniesService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * API endpoint that allows companies to be viewed or edited.
     * @returns PaginatedCompanyExtendedList
     * @throws ApiError
     */
    public companiesList({
        limit,
        name,
        offset,
        stripeCustomerId,
    }: {
        /**
         * Number of results to return per page.
         */
        limit?: number,
        name?: string,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
        stripeCustomerId?: string,
    }): CancelablePromise<PaginatedCompanyExtendedList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/',
            query: {
                'limit': limit,
                'name': name,
                'offset': offset,
                'stripe_customer_id': stripeCustomerId,
            },
        });
    }

    /**
     * API endpoint that allows companies to be viewed or edited.
     * @returns CompanyExtended
     * @throws ApiError
     */
    public companiesCreate({
        requestBody,
    }: {
        requestBody: CompanyExtendedRequest,
    }): CancelablePromise<CompanyExtended> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/companies/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns CompanyBAVSettings
     * @throws ApiError
     */
    public companiesBenefitSettingsBavRetrieve({
        id,
    }: {
        id: string,
    }): CancelablePromise<CompanyBAVSettings> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{id}/benefit-settings/bav',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @returns CompanyBAVSettingsPatch
     * @throws ApiError
     */
    public companiesBenefitSettingsBavUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: CompanyBAVSettingsPatchRequest,
    }): CancelablePromise<CompanyBAVSettingsPatch> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/companies/{id}/benefit-settings/bav',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns CompanyBAVSettingsPatch
     * @throws ApiError
     */
    public companiesBenefitSettingsBavPartialUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: PatchedCompanyBAVSettingsPatchRequest,
    }): CancelablePromise<CompanyBAVSettingsPatch> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/companies/{id}/benefit-settings/bav',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @deprecated
     * @returns CompanyCustomBenefit
     * @throws ApiError
     */
    public companiesBenefitSettingsCustomRetrieve({
        id,
    }: {
        id: string,
    }): CancelablePromise<CompanyCustomBenefit> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{id}/benefit-settings/custom',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @deprecated
     * @returns CompanyCustomBenefit
     * @throws ApiError
     */
    public companiesBenefitSettingsCustomCreate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody: CompanyCustomBenefitRequest,
    }): CancelablePromise<CompanyCustomBenefit> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/companies/{id}/benefit-settings/custom',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @deprecated
     * @returns CompanyCustomBenefit
     * @throws ApiError
     */
    public companiesBenefitSettingsCustomUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody: CompanyCustomBenefitRequest,
    }): CancelablePromise<CompanyCustomBenefit> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/companies/{id}/benefit-settings/custom',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @deprecated
     * @returns CompanyCustomBenefit
     * @throws ApiError
     */
    public companiesBenefitSettingsCustomPartialUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: PatchedCompanyCustomBenefitRequest,
    }): CancelablePromise<CompanyCustomBenefit> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/companies/{id}/benefit-settings/custom',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @deprecated
     * @returns CompanyCustomBenefitDetail
     * @throws ApiError
     */
    public companiesBenefitSettingsCustomRetrieve2({
        benefitId,
        id,
    }: {
        benefitId: string,
        id: string,
    }): CancelablePromise<CompanyCustomBenefitDetail> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{id}/benefit-settings/custom/{benefit_id}',
            path: {
                'benefit_id': benefitId,
                'id': id,
            },
        });
    }

    /**
     * @deprecated
     * @returns CompanyCustomBenefitPatch
     * @throws ApiError
     */
    public companiesBenefitSettingsCustomUpdate2({
        benefitId,
        id,
        requestBody,
    }: {
        benefitId: string,
        id: string,
        requestBody: CompanyCustomBenefitPatchRequest,
    }): CancelablePromise<CompanyCustomBenefitPatch> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/companies/{id}/benefit-settings/custom/{benefit_id}',
            path: {
                'benefit_id': benefitId,
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @deprecated
     * @returns CompanyCustomBenefitPatch
     * @throws ApiError
     */
    public companiesBenefitSettingsCustomPartialUpdate2({
        benefitId,
        id,
        requestBody,
    }: {
        benefitId: string,
        id: string,
        requestBody?: PatchedCompanyCustomBenefitPatchRequest,
    }): CancelablePromise<CompanyCustomBenefitPatch> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/companies/{id}/benefit-settings/custom/{benefit_id}',
            path: {
                'benefit_id': benefitId,
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @deprecated
     * @returns void
     * @throws ApiError
     */
    public companiesBenefitSettingsCustomDestroy({
        benefitId,
        id,
    }: {
        benefitId: string,
        id: string,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/companies/{id}/benefit-settings/custom/{benefit_id}',
            path: {
                'benefit_id': benefitId,
                'id': id,
            },
        });
    }

    /**
     * @returns CompanyJobbikeSettings
     * @throws ApiError
     */
    public companiesBenefitSettingsJobbikeRetrieve({
        id,
    }: {
        id: string,
    }): CancelablePromise<CompanyJobbikeSettings> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{id}/benefit-settings/jobbike',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @returns CompanyJobbikeSettingsPatch
     * @throws ApiError
     */
    public companiesBenefitSettingsJobbikeUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: CompanyJobbikeSettingsPatchRequest,
    }): CancelablePromise<CompanyJobbikeSettingsPatch> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/companies/{id}/benefit-settings/jobbike',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns CompanyJobbikeSettingsPatch
     * @throws ApiError
     */
    public companiesBenefitSettingsJobbikePartialUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: PatchedCompanyJobbikeSettingsPatchRequest,
    }): CancelablePromise<CompanyJobbikeSettingsPatch> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/companies/{id}/benefit-settings/jobbike',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns CompanyMPPSettings
     * @throws ApiError
     */
    public companiesBenefitSettingsMppRetrieve({
        id,
    }: {
        id: string,
    }): CancelablePromise<CompanyMPPSettings> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{id}/benefit-settings/mpp',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @returns CompanyMPPSettings
     * @throws ApiError
     */
    public companiesBenefitSettingsMppUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody: CompanyMPPSettingsRequest,
    }): CancelablePromise<CompanyMPPSettings> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/companies/{id}/benefit-settings/mpp',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns CompanyMPPSettings
     * @throws ApiError
     */
    public companiesBenefitSettingsMppPartialUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: PatchedCompanyMPPSettingsRequest,
    }): CancelablePromise<CompanyMPPSettings> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/companies/{id}/benefit-settings/mpp',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns CompanyContactData
     * @throws ApiError
     */
    public companiesContactDataRetrieve({
        id,
    }: {
        id: string,
    }): CancelablePromise<CompanyContactData> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{id}/contact-data',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @returns CompanyContactData
     * @throws ApiError
     */
    public companiesContactDataUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: CompanyContactDataRequest,
    }): CancelablePromise<CompanyContactData> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/companies/{id}/contact-data',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns CompanyContactData
     * @throws ApiError
     */
    public companiesContactDataPartialUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: PatchedCompanyContactDataRequest,
    }): CancelablePromise<CompanyContactData> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/companies/{id}/contact-data',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns any No response body
     * @throws ApiError
     */
    public companiesDocumentsRetrieve({
        id,
        type,
    }: {
        id: string,
        type?: 'OTHER' | 'PAYROLL' | 'SV' | 'WAGE_TAX',
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{id}/documents',
            path: {
                'id': id,
            },
            query: {
                'type': type,
            },
        });
    }

    /**
     * @returns any No response body
     * @throws ApiError
     */
    public companiesDocumentsUpdate({
        id,
        type,
    }: {
        id: string,
        type?: 'OTHER' | 'PAYROLL' | 'SV' | 'WAGE_TAX',
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/companies/{id}/documents',
            path: {
                'id': id,
            },
            query: {
                'type': type,
            },
        });
    }

    /**
     * @returns any No response body
     * @throws ApiError
     */
    public companiesDocumentsPartialUpdate({
        id,
        type,
    }: {
        id: string,
        type?: 'OTHER' | 'PAYROLL' | 'SV' | 'WAGE_TAX',
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/companies/{id}/documents',
            path: {
                'id': id,
            },
            query: {
                'type': type,
            },
        });
    }

    /**
     * @returns CompanyGeneralData
     * @throws ApiError
     */
    public companiesGeneralDataRetrieve({
        id,
    }: {
        id: string,
    }): CancelablePromise<CompanyGeneralData> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{id}/general-data',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @returns CompanyGeneralData
     * @throws ApiError
     */
    public companiesGeneralDataUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody: CompanyGeneralDataRequest,
    }): CancelablePromise<CompanyGeneralData> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/companies/{id}/general-data',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns CompanyGeneralData
     * @throws ApiError
     */
    public companiesGeneralDataPartialUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: PatchedCompanyGeneralDataRequest,
    }): CancelablePromise<CompanyGeneralData> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/companies/{id}/general-data',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns CompanyPaymentData
     * @throws ApiError
     */
    public companiesPaymentDataRetrieve({
        id,
    }: {
        id: string,
    }): CancelablePromise<CompanyPaymentData> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{id}/payment-data',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @returns CompanyPaymentData
     * @throws ApiError
     */
    public companiesPaymentDataUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody: CompanyPaymentDataRequest,
    }): CancelablePromise<CompanyPaymentData> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/companies/{id}/payment-data',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns CompanyPaymentData
     * @throws ApiError
     */
    public companiesPaymentDataPartialUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: PatchedCompanyPaymentDataRequest,
    }): CancelablePromise<CompanyPaymentData> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/companies/{id}/payment-data',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns CompanySettings
     * @throws ApiError
     */
    public companiesSettingsRetrieve({
        id,
    }: {
        id: string,
    }): CancelablePromise<CompanySettings> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{id}/settings',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @returns CompanySettings
     * @throws ApiError
     */
    public companiesSettingsUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: CompanySettingsRequest,
    }): CancelablePromise<CompanySettings> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/companies/{id}/settings',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns CompanySettings
     * @throws ApiError
     */
    public companiesSettingsPartialUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: PatchedCompanySettingsRequest,
    }): CancelablePromise<CompanySettings> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/companies/{id}/settings',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns CompanyAppSettings
     * @throws ApiError
     */
    public companiesSettingsAppRetrieve({
        id,
    }: {
        id: string,
    }): CancelablePromise<CompanyAppSettings> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{id}/settings/app',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @returns CompanyAppSettings
     * @throws ApiError
     */
    public companiesSettingsAppUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: CompanyAppSettingsRequest,
    }): CancelablePromise<CompanyAppSettings> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/companies/{id}/settings/app',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns CompanyAppSettings
     * @throws ApiError
     */
    public companiesSettingsAppPartialUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: PatchedCompanyAppSettingsRequest,
    }): CancelablePromise<CompanyAppSettings> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/companies/{id}/settings/app',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns PayrollExportMapping
     * @throws ApiError
     */
    public companiesSettingsPayrollRetrieve({
        id,
    }: {
        id: string,
    }): CancelablePromise<PayrollExportMapping> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{id}/settings/payroll',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @returns PayrollExportMapping
     * @throws ApiError
     */
    public companiesSettingsPayrollUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: PayrollExportMappingRequest,
    }): CancelablePromise<PayrollExportMapping> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/companies/{id}/settings/payroll',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns PayrollExportMapping
     * @throws ApiError
     */
    public companiesSettingsPayrollPartialUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: PatchedPayrollExportMappingRequest,
    }): CancelablePromise<PayrollExportMapping> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/companies/{id}/settings/payroll',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns PaginatedKomboSyncList
     * @throws ApiError
     */
    public companiesHrisList({
        parentCompany,
        page,
        pageSize,
    }: {
        parentCompany: string,
        /**
         * A page number within the paginated result set.
         */
        page?: number,
        /**
         * Number of results to return per page.
         */
        pageSize?: number,
    }): CancelablePromise<PaginatedKomboSyncList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{parent_company}/hris/',
            path: {
                'parent_company': parentCompany,
            },
            query: {
                'page': page,
                'page_size': pageSize,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns KomboSync
     * @throws ApiError
     */
    public companiesHrisCreate({
        parentCompany,
        requestBody,
    }: {
        parentCompany: string,
        requestBody?: KomboSyncRequest,
    }): CancelablePromise<KomboSync> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/companies/{parent_company}/hris/',
            path: {
                'parent_company': parentCompany,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns KomboSync
     * @throws ApiError
     */
    public companiesHrisRetrieve({
        id,
        parentCompany,
    }: {
        id: string,
        parentCompany: string,
    }): CancelablePromise<KomboSync> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{parent_company}/hris/{id}/',
            path: {
                'id': id,
                'parent_company': parentCompany,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns KomboSync
     * @throws ApiError
     */
    public companiesHrisUpdate({
        id,
        parentCompany,
        requestBody,
    }: {
        id: string,
        parentCompany: string,
        requestBody?: KomboSyncRequest,
    }): CancelablePromise<KomboSync> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/companies/{parent_company}/hris/{id}/',
            path: {
                'id': id,
                'parent_company': parentCompany,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns KomboSync
     * @throws ApiError
     */
    public companiesHrisPartialUpdate({
        id,
        parentCompany,
        requestBody,
    }: {
        id: string,
        parentCompany: string,
        requestBody?: PatchedKomboSyncRequest,
    }): CancelablePromise<KomboSync> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/companies/{parent_company}/hris/{id}/',
            path: {
                'id': id,
                'parent_company': parentCompany,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns void
     * @throws ApiError
     */
    public companiesHrisDestroy({
        id,
        parentCompany,
    }: {
        id: string,
        parentCompany: string,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/companies/{parent_company}/hris/{id}/',
            path: {
                'id': id,
                'parent_company': parentCompany,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns KomboLink
     * @throws ApiError
     */
    public companiesHrisCreateKomboLinkCreate({
        parentCompany,
    }: {
        parentCompany: string,
    }): CancelablePromise<KomboLink> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/companies/{parent_company}/hris/create-kombo-link/',
            path: {
                'parent_company': parentCompany,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns void
     * @throws ApiError
     */
    public companiesHrisDeleteHrisIntegrationDestroy({
        parentCompany,
    }: {
        parentCompany: string,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/companies/{parent_company}/hris/delete-hris-integration/',
            path: {
                'parent_company': parentCompany,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns ForceKomboSync
     * @throws ApiError
     */
    public companiesHrisForceHrisSyncCreate({
        parentCompany,
    }: {
        parentCompany: string,
    }): CancelablePromise<ForceKomboSync> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/companies/{parent_company}/hris/force-hris-sync/',
            path: {
                'parent_company': parentCompany,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns HrisSetupStatus
     * @throws ApiError
     */
    public companiesHrisHrisSetupStatusRetrieve({
        parentCompany,
    }: {
        parentCompany: string,
    }): CancelablePromise<HrisSetupStatus> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{parent_company}/hris/hris-setup-status/',
            path: {
                'parent_company': parentCompany,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns KomboSync
     * @throws ApiError
     */
    public companiesHrisPreviewHrisEmployeesRetrieve({
        parentCompany,
    }: {
        parentCompany: string,
    }): CancelablePromise<KomboSync> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{parent_company}/hris/preview-hris-employees/',
            path: {
                'parent_company': parentCompany,
            },
        });
    }

    /**
     * API endpoint that allows companies to be viewed or edited.
     * @returns CompanyExtended
     * @throws ApiError
     */
    public companiesRetrieve({
        id,
    }: {
        id: string,
    }): CancelablePromise<CompanyExtended> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * API endpoint that allows companies to be viewed or edited.
     * @returns CompanyExtended
     * @throws ApiError
     */
    public companiesUpdate({
        id,
        requestBody,
    }: {
        /**
         * A UUID string identifying this company.
         */
        id: string,
        requestBody: CompanyExtendedRequest,
    }): CancelablePromise<CompanyExtended> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/companies/{id}/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint that allows companies to be viewed or edited.
     * @returns CompanyExtended
     * @throws ApiError
     */
    public companiesPartialUpdate({
        id,
        requestBody,
    }: {
        /**
         * A UUID string identifying this company.
         */
        id: string,
        requestBody?: PatchedCompanyExtendedRequest,
    }): CancelablePromise<CompanyExtended> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/companies/{id}/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint that allows companies to be viewed or edited.
     * @returns void
     * @throws ApiError
     */
    public companiesDestroy({
        id,
    }: {
        /**
         * A UUID string identifying this company.
         */
        id: string,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/companies/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * API endpoint that allows companies to be viewed or edited.
     * @returns CompanyBenefitSettings
     * @throws ApiError
     */
    public companiesBenefitSettingsRetrieve({
        id,
    }: {
        /**
         * A UUID string identifying this company.
         */
        id: string,
    }): CancelablePromise<CompanyBenefitSettings> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{id}/benefit-settings/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * API endpoint that allows companies to be viewed or edited.
     * @returns ReferralLink
     * @throws ApiError
     */
    public companiesReferralLinkRetrieve({
        id,
    }: {
        /**
         * A UUID string identifying this company.
         */
        id: string,
    }): CancelablePromise<ReferralLink> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{id}/referral-link/',
            path: {
                'id': id,
            },
        });
    }

}
